const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

console.log('API URL:', API_URL);

const API_ENDPOINTS = {
  TEXT_TO_VIDEO: 'https://video-rtc.ray-dev.gcp.picsartstage2.com/magi-t2v/v1/text-to-video-magi/1.0.0/model_magi_1/async',
  IMAGE_TO_VIDEO: 'https://video-rtc.ray-dev.gcp.picsartstage2.com/magi-i2v/v1/text-to-video-magi/1.0.0/model_magi_1/async',
  STATUS_TEXT_TO_VIDEO: (jobId) => `https://video-rtc.ray-dev.gcp.picsartstage2.com/magi-t2v/v1/text-to-video-magi/1.0.0/status/${jobId}`,
  STATUS_IMAGE_TO_VIDEO: (jobId) => `https://video-rtc.ray-dev.gcp.picsartstage2.com/magi-i2v/v1/text-to-video-magi/1.0.0/status/${jobId}`,
};

// Helper function to get the authentication token
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Helper function to encode URL parameters
const encodeURL = (url) => {
  return encodeURIComponent(url);
};

// Helper function to handle API responses
const handleResponse = async (response) => {
  // Check if the response is JSON
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    const data = await response.json();

    if (!response.ok) {
      // If the server returned an error with a JSON body
      const errorMessage = data.error || data.message || `HTTP error! status: ${response.status}`;
      console.error('API error:', errorMessage);
      throw new Error(errorMessage);
    }

    return data;
  } else {
    // If the response is not JSON (likely HTML error page)
    const text = await response.text();
    console.error('Non-JSON response received:', text.substring(0, 150) + '...');
    throw new Error(`Server returned non-JSON response with status ${response.status}`);
  }
};

export const login = async (token) => {
  try {
    console.log('Attempting to login with token:', token ? 'Token provided' : 'No token');

    const response = await fetch(`${API_URL}/auth/google`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ token })
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('API error in login:', error);
    throw error;
  }
};

// Submit text to video generation request
export const generateTextToVideo = async (prompt, advancedParams = {}) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }

    console.log('Sending generation request with prompt:', prompt);
    console.log('Advanced parameters:', advancedParams);

    // Format the request body according to the OpenAPI specification
    const requestBody = {
      prompt: prompt,
      size: advancedParams.size || [1280, 720],
      video_length: advancedParams.video_length || 5,
      num_steps: advancedParams.sampling_steps || 16,
      seed: advancedParams.seed !== undefined ? advancedParams.seed : -1,
      use_prompt_extend: advancedParams.use_prompt_extend !== undefined ? advancedParams.use_prompt_extend : false,
    };

    const targetUrl = encodeURL(API_ENDPOINTS.TEXT_TO_VIDEO);
    const response = await fetch(`${API_URL}/proxy?url=${targetUrl}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestBody)
    });

    const data = await response.json();
    console.log('Text-to-video API full response:', data);

    if (!response.ok) {
      console.error('Error response:', data);
      throw new Error(JSON.stringify(data.error || `HTTP error! status: ${response.status}`));
    }

    // Extract job_id from the response based on the OpenAPI structure
    let jobId = null;

    if (data.id) {
      jobId = data.id;
    } else if (data.response && data.response.id) {
      jobId = data.response.id;
    }

    // If we found a job ID, return it in a consistent format
    if (jobId) {
      console.log('Found job ID:', jobId);
      return { job_id: jobId, originalResponse: data };
    } else {
      console.error('No job ID found in response:', data);
      throw new Error('No job ID found in server response');
    }
  } catch (error) {
    console.error('API error in generateTextToVideo:', error);
    throw error;
  }
};

// Submit image to video generation request
export const generateImageToVideo = async (imageUrl, prompt, resolution = '720p', advancedParams = {}) => {
  try {
    const apiEndpoint = API_ENDPOINTS.IMAGE_TO_VIDEO;

    const defaultSize = resolution === '720p' ? [1280, 720] : [832, 480];

    console.log(`Sending image-to-video request to ${apiEndpoint}:`, {
      prompt: prompt,
      imageUrl: imageUrl,
      advancedParams: advancedParams
    });

    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    // Format the request body according to the API requirements
    const requestBody = {
      use_prompt_extend: advancedParams.use_prompt_extend !== undefined ? advancedParams.use_prompt_extend : false,
      prompt: prompt,
      video_length: advancedParams.video_length || 5,
      num_steps: advancedParams.sampling_steps || 16,
      seed: advancedParams.seed !== undefined ? advancedParams.seed : -1,
      image: imageUrl
    };

    const targetUrl = encodeURL(apiEndpoint);
    const response = await fetch(`${API_URL}/proxy?url=${targetUrl}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestBody),
    });

    const data = await response.json();
    console.log('Image-to-video API full response:', data);

    if (!response.ok) {
      console.error('Error response:', data);
      throw new Error(JSON.stringify(data.error || `HTTP error! status: ${response.status}`));
    }

    // Extract job_id from the response based on the OpenAPI structure
    let jobId = null;

    if (data.id) {
      jobId = data.id;
    } else if (data.response && data.response.id) {
      jobId = data.response.id;
    }

    // If we found a job ID, return it in a consistent format
    if (jobId) {
      console.log('Found job ID:', jobId);
      return { job_id: jobId, originalResponse: data };
    } else {
      console.error('No job ID found in response:', data);
      throw new Error('No job ID found in server response');
    }
  } catch (error) {
    console.error('Error generating image to video:', error);
    throw error;
  }
};

// Check generation status
export const checkGenerationStatus = async (jobId, generationType) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }

    console.log(`Checking status for job ${jobId}`);

    // Determine which status endpoint to use based on generation type
    let statusUrl;
    if (generationType === 'text-to-video') {
      statusUrl = API_ENDPOINTS.STATUS_TEXT_TO_VIDEO(jobId);
    } else if (generationType === 'image-to-video') {
      statusUrl = API_ENDPOINTS.STATUS_IMAGE_TO_VIDEO(jobId);
    } else {
      throw new Error(`Unknown generation type: ${generationType}`);
    }

    const targetUrl = encodeURL(statusUrl);
    const response = await fetch(`${API_URL}/proxy?url=${targetUrl}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    const data = await response.json();
    console.log('Status check response:', data);

    // Process the response according to the OpenAPI specification
    if (data.status === 'SUCCESSFUL' && data.response && data.response.data) {
      // Extract the video URL from the response
      const videoUrl = data.response.data.result;
      return {
        status: 'completed',
        video_url: videoUrl,
        extended_prompt: data.response.data.extended_prompt,
        originalResponse: data
      };
    }

    return data;
  } catch (error) {
    console.error('API error in checkGenerationStatus:', error);
    throw error;
  }
};

export const getUserProfile = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_URL}/user/profile`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('API error in getUserProfile:', error);
    throw error;
  }
};
